import React, { useState } from "react";
import Layout from "@/components/layout/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { IrradiacaoCepInput } from "@/components/calculadora/IrradiacaoCepInput";
import { DadosClienteForm } from "@/components/calculadora/DadosClienteForm";
import { DadosInstalacaoForm } from "@/components/calculadora/DadosInstalacaoForm";
import { KitSolarDisplay } from "@/components/calculadora/KitSolarDisplay";
import { PropostaComercial } from "@/components/calculadora/PropostaComercial";
import { 
  DadosCliente, 
  DadosInstalacao, 
  DadosIrradiacao, 
  KitSolar, 
  ParametrosFinanceiros,
  EquipamentoSolar
} from "@/types";
import { 
  ArrowRight, 
  ArrowLeft, 
  SunMedium, 
  Users, 
  Home, 
  Calculator, 
  Package,
  BarChart3,
  Save,
  List
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const CalculadoraSolarPage = () => {
  const { user, canAccessCalculator } = useAuth();
  const navigate = useNavigate();
  
  if (user && !canAccessCalculator()) {
    toast.error("Você não tem permissão para acessar esta página");
    navigate("/dashboard");
    return null;
  }
  
  const [activeTab, setActiveTab] = useState("irradiacao");
  const [dadosIrradiacao, setDadosIrradiacao] = useState<DadosIrradiacao | null>(null);
  const [dadosCliente, setDadosCliente] = useState<DadosCliente | null>(null);
  const [dadosInstalacao, setDadosInstalacao] = useState<DadosInstalacao | null>(null);
  const [parametrosFinanceiros, setParametrosFinanceiros] = useState<ParametrosFinanceiros | null>(null);
  const [kitSolar, setKitSolar] = useState<KitSolar | null>(null);
  
  const proximaEtapa = (proximaAba: string) => {
    setActiveTab(proximaAba);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  
  const etapaAnterior = (abaAnterior: string) => {
    if (abaAnterior === "instalacao" && activeTab === "kit" && dadosInstalacao) {
      const consumoMensal = dadosInstalacao.consumoMensal;
      const consumoMensalDetalhado = dadosInstalacao.consumoMensalDetalhado;
      const dadosConsumoMensal = dadosInstalacao.dadosConsumoMensal;
      const consumoHoraPonta = dadosInstalacao.consumoHoraPonta;
      const consumoForaPonta = dadosInstalacao.consumoForaPonta;
      const tipoTensao = dadosInstalacao.tipoTensao;
      const tipologia = dadosInstalacao.tipologia;
      const concessionaria = dadosInstalacao.concessionaria;
      const tipoTarifa = dadosInstalacao.tipoTarifa;
      const demandaContratada = dadosInstalacao.demandaContratada;
      
      const dadosResetados: DadosInstalacao = {
        tipoTensao,
        difusaoAzimutal: 0,
        inclinacaoTelhado: 15,
        tipologia,
        consumoMensal,
        areaDeTelhado: 0,
        tipoTelhado: "",
        concessionaria,
        tipoTarifa,
        consumoMensalDetalhado,
        dadosConsumoMensal
      };
      
      if (tipoTensao === "media") {
        dadosResetados.consumoHoraPonta = consumoHoraPonta;
        dadosResetados.consumoForaPonta = consumoForaPonta;
        dadosResetados.demandaContratada = demandaContratada;
      }
      
      setDadosInstalacao(dadosResetados);
    }
    
    setActiveTab(abaAnterior);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  
  const handleDadosIrradiacao = (dados: DadosIrradiacao) => {
    setDadosIrradiacao(dados);
    proximaEtapa("cliente");
  };
  
  const handleDadosCliente = (dados: DadosCliente) => {
    setDadosCliente(dados);
    proximaEtapa("instalacao");
  };
  
  const handleDadosInstalacao = (dados: DadosInstalacao) => {
    setDadosInstalacao(dados);
    proximaEtapa("kit");
  };
  
  const handleKitSelecionado = (kit: KitSolar) => {
    setKitSolar(kit);
  };
  
  const handleParametrosFinanceiros = (dados: ParametrosFinanceiros) => {
    setParametrosFinanceiros(dados);
  };
  
  const podeAvancarParaProposta = kitSolar !== null && parametrosFinanceiros !== null;
  
  const irParaProposta = () => {
    if (podeAvancarParaProposta) {
      proximaEtapa("proposta");
    } else {
      toast.error("Selecione um kit e defina os parâmetros financeiros primeiro");
    }
  };

  const irParaConfiguracoes = () => {
    navigate("/solar/configuracao");
  };

  const salvarComponentesParaReutilizacao = () => {
    if (kitSolar) {
      // Salvando os painéis
      if (kitSolar.paineis && kitSolar.paineis.length > 0) {
        const paineisSalvos = localStorage.getItem('paineisCadastrados');
        let paineis: EquipamentoSolar[] = paineisSalvos ? JSON.parse(paineisSalvos) : [];
        
        kitSolar.paineis.forEach(painel => {
          // Verificar se já existe um painel com marca e modelo idênticos
          const painelExistente = paineis.find(
            p => p.marca === painel.marca && p.modelo === painel.modelo
          );
          
          if (!painelExistente) {
            paineis.push({
              ...painel,
              tipo: 'painel',
              disponibilidade: true
            });
          }
        });
        
        localStorage.setItem('paineisCadastrados', JSON.stringify(paineis));
      }
      
      // Salvando os inversores
      if (kitSolar.inversores && kitSolar.inversores.length > 0) {
        const inversoresSalvos = localStorage.getItem('inversoresCadastrados');
        let inversores: EquipamentoSolar[] = inversoresSalvos ? JSON.parse(inversoresSalvos) : [];
        
        kitSolar.inversores.forEach(inversor => {
          // Verificar se já existe um inversor com marca e modelo idênticos
          const inversorExistente = inversores.find(
            i => i.marca === inversor.marca && i.modelo === inversor.modelo
          );
          
          if (!inversorExistente) {
            inversores.push({
              ...inversor,
              tipo: 'inversor',
              disponibilidade: true
            });
          }
        });
        
        localStorage.setItem('inversoresCadastrados', JSON.stringify(inversores));
      }
      
      // Salvando outros equipamentos
      if (kitSolar.outrosEquipamentos && kitSolar.outrosEquipamentos.length > 0) {
        const outrosEquipamentosSalvos = localStorage.getItem('outrosEquipamentosCadastrados');
        let outrosEquipamentos: EquipamentoSolar[] = outrosEquipamentosSalvos ? JSON.parse(outrosEquipamentosSalvos) : [];
        
        kitSolar.outrosEquipamentos.forEach(outro => {
          // Verificar se já existe um equipamento com marca e modelo idênticos
          const equipamentoExistente = outrosEquipamentos.find(
            e => e.marca === outro.marca && e.modelo === outro.modelo
          );
          
          if (!equipamentoExistente) {
            outrosEquipamentos.push({
              ...outro,
              tipo: 'outros',
              disponibilidade: true
            });
          }
        });
        
        localStorage.setItem('outrosEquipamentosCadastrados', JSON.stringify(outrosEquipamentos));
      }
      
      // Salvando o próprio kit
      const kitsSalvos = localStorage.getItem('kitsSolaresSalvos');
      let kits: KitSolar[] = kitsSalvos ? JSON.parse(kitsSalvos) : [];
      
      // Verificar se já existe um kit com o mesmo nome
      const kitExistente = kits.findIndex(k => k.id === kitSolar.id);
      
      if (kitExistente >= 0) {
        // Atualizar o kit existente
        kits[kitExistente] = kitSolar;
      } else {
        // Adicionar novo kit
        kits.push(kitSolar);
      }
      
      localStorage.setItem('kitsSolaresSalvos', JSON.stringify(kits));
      
      toast.success("Componentes e kit solar salvos com sucesso para reutilização futura!");
    } else {
      toast.error("Não há um kit solar selecionado para salvar");
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-screen-xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-green-700 flex items-center gap-2">
              <SunMedium className="h-8 w-8" /> Calculadora Solar
            </h1>
            <p className="text-gray-600 mt-2">
              Dimensione sistemas fotovoltaicos, gere propostas comerciais e encontre os melhores equipamentos.
            </p>
          </div>
          <div className="flex gap-2">
            {kitSolar && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={salvarComponentesParaReutilizacao} 
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" /> Salvar Componentes
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate("/solar")} 
              className="flex items-center gap-2"
            >
              <BarChart3 className="h-4 w-4" /> Voltar ao Dashboard
            </Button>
          </div>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
          <TabsList className="grid grid-cols-2 md:grid-cols-5 gap-4 w-full h-auto p-1">
            <TabsTrigger 
              value="irradiacao" 
              className="flex flex-col gap-1 py-3 md:py-4 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
            >
              <SunMedium className="h-5 w-5" />
              <span className="text-xs sm:text-sm">Irradiação</span>
            </TabsTrigger>
            <TabsTrigger 
              value="cliente" 
              className="flex flex-col gap-1 py-3 md:py-4 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
              disabled={dadosIrradiacao === null}
            >
              <Users className="h-5 w-5" />
              <span className="text-xs sm:text-sm">Cliente</span>
            </TabsTrigger>
            <TabsTrigger 
              value="instalacao" 
              className="flex flex-col gap-1 py-3 md:py-4 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
              disabled={dadosCliente === null}
            >
              <Home className="h-5 w-5" />
              <span className="text-xs sm:text-sm">Instalação</span>
            </TabsTrigger>
            <TabsTrigger 
              value="kit" 
              className="flex flex-col gap-1 py-3 md:py-4 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
              disabled={dadosInstalacao === null}
            >
              <Package className="h-5 w-5" />
              <span className="text-xs sm:text-sm">Kit Solar</span>
            </TabsTrigger>
            <TabsTrigger 
              value="proposta" 
              className="flex flex-col gap-1 py-3 md:py-4 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
              disabled={!podeAvancarParaProposta}
            >
              <Calculator className="h-5 w-5" />
              <span className="text-xs sm:text-sm">Proposta</span>
            </TabsTrigger>
          </TabsList>
          
          <div className="mt-6">
            <TabsContent value="irradiacao">
              <IrradiacaoCepInput 
                onDadosCarregados={handleDadosIrradiacao} 
                onVoltar={() => navigate("/solar")}
              />
              
              {dadosIrradiacao && (
                <div className="mt-6 space-y-4">
                  <h2 className="text-xl font-semibold">Dados de Irradiação</h2>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Irradiação Média</p>
                      <p className="text-2xl font-bold text-green-600">{dadosIrradiacao.irradiacaoMedia.toFixed(2)} kWh/m²/dia</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Mês com Menor Irradiação</p>
                      <p className="text-xl font-bold">{dadosIrradiacao.mesMin} ({dadosIrradiacao.minIrradiacao.toFixed(2)})</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Mês com Maior Irradiação</p>
                      <p className="text-xl font-bold">{dadosIrradiacao.mesMax} ({dadosIrradiacao.maxIrradiacao.toFixed(2)})</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-end mt-6">
                    <Button onClick={() => proximaEtapa("cliente")}>
                      Próximo <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="cliente">
              <DadosClienteForm 
                onDadosSalvos={handleDadosCliente} 
                dadosIniciais={dadosCliente || undefined}
                onVoltar={() => etapaAnterior("irradiacao")}
              />
              
              <div className="flex justify-between mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => etapaAnterior("irradiacao")}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
                </Button>
                
                {dadosCliente && (
                  <Button onClick={() => proximaEtapa("instalacao")}>
                    Próximo <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="instalacao">
              <DadosInstalacaoForm 
                onDadosSalvos={handleDadosInstalacao} 
                dadosIniciais={dadosInstalacao || undefined}
                onVoltar={() => etapaAnterior("cliente")}
                dadosIrradiacao={dadosIrradiacao || undefined}
              />
              
              <div className="flex justify-between mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => etapaAnterior("cliente")}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
                </Button>
                
                {dadosInstalacao && (
                  <Button onClick={() => proximaEtapa("kit")}>
                    Próximo <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="kit">
              {dadosInstalacao && dadosIrradiacao && (
                <>
                  <KitSolarDisplay 
                    dadosInstalacao={dadosInstalacao} 
                    dadosIrradiacao={dadosIrradiacao}
                    onKitSelecionado={handleKitSelecionado}
                    parametrosFinanceiros={parametrosFinanceiros}
                    onParametrosFinanceirosSalvos={handleParametrosFinanceiros}
                  />
                  
                  <div className="flex justify-between mt-8">
                    <Button 
                      variant="outline" 
                      onClick={() => etapaAnterior("instalacao")}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
                    </Button>
                    
                    <div className="flex gap-2">
                      {kitSolar && (
                        <Button 
                          variant="outline" 
                          onClick={salvarComponentesParaReutilizacao}
                          className="flex items-center gap-2"
                        >
                          <Save className="h-4 w-4" /> Salvar Componentes
                        </Button>
                      )}
                      
                      {podeAvancarParaProposta && (
                        <Button onClick={irParaProposta}>
                          Gerar Proposta <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
            
            <TabsContent value="proposta">
              {dadosCliente && dadosInstalacao && dadosIrradiacao && kitSolar && parametrosFinanceiros ? (
                <>
                  <PropostaComercial 
                    dadosCliente={dadosCliente}
                    dadosInstalacao={dadosInstalacao}
                    dadosIrradiacao={dadosIrradiacao}
                    kitSolar={kitSolar}
                    parametrosFinanceiros={parametrosFinanceiros}
                  />
                  
                  <div className="flex justify-start mt-6">
                    <Button 
                      variant="outline" 
                      onClick={() => etapaAnterior("kit")}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" /> Voltar para Kit Solar
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <h2 className="text-xl font-semibold text-gray-700">
                    Complete as etapas anteriores para gerar a proposta
                  </h2>
                  <Button 
                    className="mt-4" 
                    onClick={() => etapaAnterior("kit")}
                  >
                    Voltar para Kit Solar
                  </Button>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </Layout>
  );
};

export default CalculadoraSolarPage;
